import React, { useState, useEffect } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';

const DEFAULT_SIZES = {
  circle: { width: 90, height: 90 },
  square: { width: 90, height: 90 },
  rectangle: { width: 140, height: 90 },
};

// A restaurant onboarding pagers has no reason to add them one at a time - "Add pagers 1-30"
// is the actual request, so a bulk range is capped generously above any realistic pager count
// but still well short of a typo turning into a runaway loop of create requests.
const MAX_RANGE_COUNT = 300;

const TablePropertiesModal = ({ isOpen, table, zoneId, zoneValue, savedColors, onClose, onSaved, onDeleted }) => {
  const [name, setName] = useState('');
  const [shape, setShape] = useState('circle');
  const [numberOfPeople, setNumberOfPeople] = useState(4);
  const [color, setColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rangeMode, setRangeMode] = useState(false);
  const [rangeFrom, setRangeFrom] = useState('1');
  const [rangeTo, setRangeTo] = useState('30');
  const isEdit = !!table;
  // A pager has no shape/seats/color of its own - it's just a number - so those fields are
  // hidden and saved with harmless defaults instead of asking staff to fill in values that
  // don't mean anything for it.
  const isPager = zoneValue === 'pager-tab';

  useEffect(() => {
    if (!isOpen) return;
    if (table) {
      setName(table.name || '');
      setShape(table.shape || 'circle');
      setNumberOfPeople(table.number_of_people || 4);
      setColor(table.color || '');
    } else {
      setName('');
      setShape('circle');
      setNumberOfPeople(isPager ? 1 : 4);
      setColor('');
      setRangeMode(false);
      setRangeFrom('1');
      setRangeTo('30');
    }
  }, [isOpen, table]);

  const handleSaveRange = async () => {
    const from = parseInt(rangeFrom, 10);
    const to = parseInt(rangeTo, 10);

    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < 1) {
      toast.error('Enter a valid numeric range');
      return;
    }
    if (from > to) {
      toast.error('"From" must be less than or equal to "To"');
      return;
    }
    if (to > 999) {
      toast.error('Pager numbers can be at most 3 digits (999)');
      return;
    }
    const count = to - from + 1;
    if (count > MAX_RANGE_COUNT) {
      toast.error(`Range is too large - max ${MAX_RANGE_COUNT} pagers at once`);
      return;
    }

    setSaving(true);
    const created = [];
    const failed = [];
    for (let n = from; n <= to; n += 1) {
      try {
        const res = await callAPI(
          apiUrls.FLOOR_PLAN_CREATE_TABLE,
          {},
          'POST',
          {
            zone_id: zoneId,
            name: String(n),
            shape: 'circle',
            number_of_people: 1,
            width: DEFAULT_SIZES.circle.width,
            height: DEFAULT_SIZES.circle.height,
            position_x: 20,
            position_y: 20,
            color: null,
          },
          {}
        );
        created.push(res.data.data);
      } catch (err) {
        failed.push(n);
      }
    }

    created.forEach((pager) => onSaved(pager, true));
    setSaving(false);

    if (created.length > 0) {
      toast.success(`${created.length} pager${created.length === 1 ? '' : 's'} created`);
    }
    if (failed.length > 0) {
      toast.error(`Skipped ${failed.length} (already exist): ${failed.join(', ')}`);
    }
    if (created.length > 0) {
      onClose();
    }
  };

  const handleSave = async () => {
    if (isPager && !isEdit && rangeMode) {
      return handleSaveRange();
    }

    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(isPager ? 'Pager number is required' : 'Table name is required');
      return;
    }
    if (!/^[a-zA-Z0-9]{1,3}$/.test(trimmed)) {
      toast.error(
        isPager
          ? 'Pager number must be alphanumeric, up to 3 characters'
          : 'Table name must be alphanumeric, up to 3 characters'
      );
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const res = await callAPI(
          apiUrls.FLOOR_PLAN_UPDATE_TABLE,
          {},
          'POST',
          { id: table.id, name: trimmed, shape, number_of_people: Number(numberOfPeople), color: color || null },
          {}
        );
        onSaved(res.data.data, false);
        toast.success(isPager ? 'Pager updated' : 'Table updated');
      } else {
        const size = DEFAULT_SIZES[shape] || DEFAULT_SIZES.circle;
        const res = await callAPI(
          apiUrls.FLOOR_PLAN_CREATE_TABLE,
          {},
          'POST',
          {
            zone_id: zoneId,
            name: trimmed,
            shape,
            number_of_people: Number(numberOfPeople),
            width: size.width,
            height: size.height,
            position_x: 20,
            position_y: 20,
            color: color || null,
          },
          {}
        );
        onSaved(res.data.data, true);
        toast.success(isPager ? 'Pager created' : 'Table created');
      }
    } catch (err) {
      const message =
        err.response?.data?.errors?.name?.[0] ||
        err.response?.data?.message ||
        (isPager ? 'Failed to save pager' : 'Failed to save table');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setDeleting(true);
    try {
      await callAPI(apiUrls.FLOOR_PLAN_DELETE_TABLE, {}, 'POST', { id: table.id }, {});
      toast.success(isPager ? 'Pager deleted' : 'Table deleted');
      onDeleted(table.id);
    } catch (err) {
      toast.error('Failed to delete table');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="tablePropertiesModal">
      <div className="modal-header">
        <h5 className="modal-title">
          {isPager ? (isEdit ? 'Edit Pager' : 'Add Pager') : (isEdit ? 'Edit Table' : 'Add Table')}
        </h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body">
        {isPager && !isEdit && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="pagerRangeMode"
              checked={rangeMode}
              onChange={(e) => setRangeMode(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="pagerRangeMode">
              Add a range (e.g. 1–30) instead of one at a time
            </label>
          </div>
        )}

        {isPager && !isEdit && rangeMode ? (
          <div className="mb-3 d-flex gap-3">
            <div className="flex-fill">
              <label className="form-label">From</label>
              <input
                type="number"
                className="form-control"
                min={1}
                max={999}
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
              />
            </div>
            <div className="flex-fill">
              <label className="form-label">To</label>
              <input
                type="number"
                className="form-control"
                min={1}
                max={999}
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <label className="form-label">{isPager ? 'Pager Number' : 'Table Name'}</label>
            <input
              type="text"
              className="form-control"
              placeholder={isPager ? 'e.g. 30' : 'e.g. T1'}
              maxLength={3}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}

        {!isPager && (
          <>
            <div className="mb-3">
              <label className="form-label">Shape</label>
              <select className="form-control" value={shape} onChange={(e) => setShape(e.target.value)}>
                <option value="circle">Circle</option>
                <option value="square">Square</option>
                <option value="rectangle">Rectangle</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Number of Seats</label>
              <input
                type="number"
                className="form-control"
                min={1}
                value={numberOfPeople}
                onChange={(e) => setNumberOfPeople(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Color</label>
              <input
                type="color"
                className="form-control form-control-color"
                value={color || '#ff5722'}
                onChange={(e) => setColor(e.target.value)}
              />
              {savedColors && savedColors.length > 0 && (
                <div className="savedColorSwatches">
                  {savedColors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`colorSwatch${color === c ? ' selected' : ''}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      onClick={() => setColor(c)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="modal-footer justify-content-center">
        {isEdit && (
          <button className="btn btn-danger" onClick={handleDelete} disabled={deleting || saving}>
            {deleting ? 'Deleting...' : (isPager ? 'Delete Pager' : 'Delete Table')}
          </button>
        )}
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btnPrimary" onClick={handleSave} disabled={saving || deleting}>
          {saving
            ? (isPager && !isEdit && rangeMode ? 'Creating...' : 'Saving...')
            : (isPager && !isEdit && rangeMode ? 'Create Pagers' : 'Save')}
        </button>
      </div>
    </CommonModal>
  );
};

export default TablePropertiesModal;
